import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { tailorAPI, ordersAPI, qcAPI } from '../api';
import { 
  Package, Ruler, Scissors, CheckCircle, Truck, Clock, IndianRupee,
  ToggleLeft, ToggleRight, Bell, User, Star, MapPin, Calendar,
  Check, X, ChevronRight, Zap, AlertTriangle, Send, Leaf, Shield, ShieldCheck,
  Users, UserPlus, TrendingUp, ChevronDown, Phone, Eye, VolumeX, Volume2, Timer, Settings
} from 'lucide-react';
import { playNotificationSound, loadSettings, saveSettings } from './SoundSystem';
import useWebSocket from '../hooks/useWebSocket';
import MeasurementGuide from './tailor/MeasurementGuide';
import OrderScanMeasurements from './tailor/OrderScanMeasurements';
import CustomerHistoryPanel from './tailor/CustomerHistoryPanel';
import QCInlineForm from './QCInlineForm';

const MAX_ACTIVE_ORDERS = 8;

const TailorDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAvailable, setIsAvailable] = useState(true);
  const [soundSettings, setSoundSettings] = useState(loadSettings);
  const isMuted = !soundSettings.soundEnabled || soundSettings.nightMode;
  const [activeTab, setActiveTab] = useState('new');
  const [earningsPeriod, setEarningsPeriod] = useState('monthly');
  const [counterModal, setCounterModal] = useState(null);
  const [counterDate, setCounterDate] = useState('');
  const [showTeamPanel, setShowTeamPanel] = useState(false);
  const [assignModal, setAssignModal] = useState(null);
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [newStaffName, setNewStaffName] = useState('');
  const [newStaffRole, setNewStaffRole] = useState('');
  const [measurementModal, setMeasurementModal] = useState(null);
  const [detailsModal, setDetailsModal] = useState(null);
  const [callSession, setCallSession] = useState(null); // {call_id, proxy_number, message}
  const [callingBusy, setCallingBusy] = useState(false);
  const [zoomImage, setZoomImage] = useState(null); // {src, label}
  const pollRef = useRef(null);

  const handleMaskedCall = async (orderId) => {
    if (!orderId || callingBusy) return;
    setCallingBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tailor/call/${orderId}`, {
        method: 'POST', credentials: 'include'
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Call setup failed');
      const data = await res.json();
      setCallSession(data);
    } catch (err) {
      setCallSession({ error: err.message });
    } finally {
      setCallingBusy(false);
    }
  };

  // Redirect if not tailor
  useEffect(() => {
    if (user === false) navigate('/tailor-login');
  }, [user, navigate]);

  const loadDashboard = useCallback(async () => {
    if (!user || user.role !== 'tailor') return;
    try {
      const data = await tailorAPI.dashboard();
      setDashData(data);
      setIsAvailable(data.profile?.is_available ?? true);
    } catch {
      navigate('/tailor-login');
    } finally { setLoading(false); }
  }, [navigate, user]);

  useEffect(() => { if (user && user.role === 'tailor') loadDashboard(); }, [user, loadDashboard]);

  // Poll every 30s for new orders
  useEffect(() => {
    pollRef.current = setInterval(loadDashboard, 30000);
    return () => clearInterval(pollRef.current);
  }, [loadDashboard]);

  // Real-time WebSocket — instant updates + sound
  useWebSocket(useCallback((msg) => {
    if (!msg || !msg.type) return;
    if (msg.type === 'new_order') {
      if (soundSettings.soundEnabled) playNotificationSound('newOrder');
      loadDashboard();
    } else if (msg.type === 'order_update' || msg.type === 'cancel_order') {
      loadDashboard();
    }
  }, [loadDashboard, soundSettings.soundEnabled]));

  const orders = useMemo(() => dashData?.orders || [], [dashData]);
  const profile = dashData?.profile || {};
  const staff = profile.staff || [];
  const earnings = dashData?.earnings || { today: 0, weekly: 0, monthly: 0, total: 0 };

  const activeCount = orders.filter(o => ['placed','assigned','stitching','counter-offered'].includes(o.status)).length;
  const isOverloaded = activeCount >= MAX_ACTIVE_ORDERS;
  const lateOrders = orders.filter(o => {
    if (!o.delivery_date || ['completed','cancelled','delivered'].includes(o.status)) return false;
    return new Date(o.delivery_date) < new Date();
  });

  const tabs = useMemo(() => [
    { id: 'new', label: 'New', count: orders.filter(o => ['placed','assigned'].includes(o.status)).length },
    { id: 'progress', label: 'In Progress', count: orders.filter(o => ['stitching','counter-offered'].includes(o.status)).length },
    { id: 'qc', label: 'QC', count: orders.filter(o => ['qc_pending','qc_approved'].includes(o.status)).length },
    { id: 'ready', label: 'Ready', count: orders.filter(o => ['ready','shipped'].includes(o.status)).length },
    { id: 'overdue', label: 'Overdue', count: lateOrders.length },
    { id: 'completed', label: 'Completed', count: orders.filter(o => ['completed','delivered'].includes(o.status)).length },
  ], [orders, lateOrders]);

  const filteredOrders = useMemo(() => {
    switch (activeTab) {
      case 'new': return orders.filter(o => ['placed','assigned'].includes(o.status));
      case 'progress': return orders.filter(o => ['stitching','counter-offered'].includes(o.status));
      case 'qc': return orders.filter(o => ['qc_pending','qc_approved'].includes(o.status));
      case 'ready': return orders.filter(o => ['ready','shipped'].includes(o.status));
      case 'overdue': return lateOrders;
      case 'completed': return orders.filter(o => ['completed','delivered'].includes(o.status));
      default: return orders;
    }
  }, [activeTab, orders, lateOrders]);

  const handleToggleAvailability = async () => {
    try {
      const res = await tailorAPI.toggleAvailability();
      setIsAvailable(res.is_available);
    } catch {}
  };

  const handleAccept = async (orderId) => {
    try {
      await ordersAPI.update(orderId, { status: 'stitching' });
      playNotificationSound('payment');
      loadDashboard();
    } catch {}
  };

  const handleReject = async (orderId) => {
    try {
      await ordersAPI.update(orderId, { status: 'cancelled' });
      loadDashboard();
    } catch {}
  };

  const handleSendCounter = async () => {
    if (!counterDate || !counterModal) return;
    try {
      await ordersAPI.update(counterModal, { counter_date: counterDate });
      setCounterModal(null); setCounterDate('');
      loadDashboard();
    } catch {}
  };

  const handleMarkReady = async (orderId) => {
    // Stitching → qc_pending, then jump to QC tab so the inline form is instantly visible.
    try {
      await qcAPI.start(orderId);
      setActiveTab('qc');
      loadDashboard();
    } catch (err) {
      alert(err.message || 'Could not start QC');
    }
  };

  const handleDispatch = async (orderId) => {
    try {
      await qcAPI.dispatch(orderId);
      playNotificationSound('payment');
      loadDashboard();
    } catch (err) {
      alert(err.message || 'Could not mark ready for dispatch');
    }
  };

  const handleMarkCompleted = async (orderId) => {
    try {
      await ordersAPI.update(orderId, { status: 'completed' });
      playNotificationSound('payment');
      loadDashboard();
    } catch {}
  };

  const handleAssignStaff = async (orderId, staffId) => {
    try {
      await ordersAPI.update(orderId, { assigned_staff: staffId });
      setAssignModal(null);
      loadDashboard();
    } catch {}
  };

  const handleAddStaff = async () => {
    if (!newStaffName.trim()) return;
    try {
      await tailorAPI.addStaff({ name: newStaffName.trim(), role: newStaffRole.trim() || 'General' });
      setNewStaffName(''); setNewStaffRole(''); setShowAddStaff(false);
      loadDashboard();
    } catch {}
  };

  // Helpers
  const getDeliveryTypeInfo = (t) => {
    if (t === 'urgent') return { label: 'Urgent', icon: Zap, color: '#991B1B', bg: 'rgba(153,27,27,0.08)' };
    if (t === 'flexible') return { label: 'Flexible', icon: Leaf, color: '#166534', bg: 'rgba(22,101,52,0.08)' };
    return { label: 'Standard', icon: Clock, color: '#D4AF37', bg: 'rgba(212,175,55,0.08)' };
  };
  const getStatusStyle = (s) => {
    const m = {
      placed: { bg:'rgba(212,175,55,0.1)', text:'#D4AF37', label:'New' },
      assigned: { bg:'rgba(212,175,55,0.1)', text:'#D4AF37', label:'Assigned' },
      stitching: { bg:'rgba(229,178,185,0.2)', text:'#E5B2B9', label:'Stitching' },
      qc_pending: { bg:'rgba(212,175,55,0.15)', text:'#D4AF37', label:'QC Pending' },
      qc_approved: { bg:'rgba(22,101,52,0.08)', text:'#166534', label:'QC Approved' },
      ready: { bg:'rgba(22,101,52,0.1)', text:'#166534', label:'Ready' },
      'counter-offered': { bg:'rgba(212,175,55,0.1)', text:'#D4AF37', label:'Counter Sent' },
      completed: { bg:'rgba(22,101,52,0.1)', text:'#166534', label:'Completed' },
      delivered: { bg:'rgba(22,101,52,0.1)', text:'#166534', label:'Delivered' },
      cancelled: { bg:'rgba(153,27,27,0.08)', text:'#991B1B', label:'Cancelled' },
    };
    return m[s] || { bg:'#F5F5F5', text:'#595959', label: s };
  };
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'numeric', month:'short' }) : 'TBD';
  const getDaysLeft = (d) => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;
  const getStaffName = (id) => staff.find(s => s.id === id)?.name || null;
  const getOrderEarnings = (o) => Math.round((o.total_price || o.amount || 0) * 0.7);

  // Order Card
  const OrderCard = ({ order }) => {
    const st = getStatusStyle(order.status);
    const dl = getDeliveryTypeInfo(order.delivery_type);
    const DlIcon = dl.icon;
    const days = getDaysLeft(order.delivery_date);
    const assignee = getStaffName(order.assigned_staff);
    const earn = getOrderEarnings(order);
    const isLate = days !== null && days < 0 && !['completed','delivered','cancelled'].includes(order.status);

    return (
      <div className="p-3 animate-fadeIn" data-testid={`order-card-${order.order_id}`}>
        {isLate && (
          <div className="flex items-center gap-1.5 mb-2 px-2 py-1.5" style={{ backgroundColor:'rgba(153,27,27,0.05)' }}>
            <AlertTriangle className="w-3 h-3" style={{ color:'#991B1B' }} strokeWidth={2} />
            <span className="text-[10px] font-medium" style={{ color:'#991B1B' }}>OVERDUE</span>
          </div>
        )}
        <div className="flex items-start justify-between mb-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
              <p className="text-[13px] font-medium" style={{ color:'#0F0F0F' }}>{order.customer_name}</p>
              <span className="px-1.5 py-px text-[9px]" style={{ backgroundColor:st.bg, color:st.text }}>{st.label}</span>
              <span className="px-1.5 py-px text-[9px] flex items-center gap-0.5" style={{ backgroundColor:dl.bg, color:dl.color }}>
                <DlIcon className="w-2.5 h-2.5" strokeWidth={1.5} />{dl.label}
              </span>
            </div>
            <p className="text-[11px]" style={{ color:'#595959' }}>{order.order_id} {'\u2022'} {order.product}</p>
          </div>
          <div className="text-right flex-shrink-0 ml-2">
            <p className="text-[13px] font-medium" style={{ color:'#0F0F0F' }}>{'\u20B9'}{(order.total_price||0).toLocaleString('en-IN')}</p>
            <p className="text-[10px]" style={{ color:'#166534' }}>Earn {'\u20B9'}{earn.toLocaleString('en-IN')}</p>
          </div>
        </div>
        <div className="flex items-center justify-between mb-2 px-2.5 py-2" style={{ backgroundColor:'#FAF9F6' }}>
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" style={{ color:'#D4AF37' }} strokeWidth={1.5} />
            <span className="text-[11px]" style={{ color:'#0F0F0F' }}>{formatDate(order.delivery_date)}</span>
          </div>
          {days !== null && !['completed','delivered','cancelled'].includes(order.status) && (
            <span className="text-[10px] px-1.5 py-0.5" style={{ backgroundColor: days<=1?'rgba(153,27,27,0.08)':days<=3?'rgba(212,175,55,0.08)':'rgba(22,101,52,0.08)', color: days<=1?'#991B1B':days<=3?'#D4AF37':'#166534' }}>
              {days<=0?'Overdue':`${days}d left`}
            </span>
          )}
        </div>
        {order.status==='counter-offered' && order.counter_date && (
          <div className="mb-2 px-2.5 py-2 border" style={{ borderColor:'rgba(212,175,55,0.2)', backgroundColor:'rgba(212,175,55,0.03)' }}>
            <p className="text-[10px]" style={{ color:'#D4AF37' }}>Counter: {formatDate(order.counter_date)} — Awaiting approval</p>
          </div>
        )}
        {assignee && (
          <div className="flex items-center gap-1.5 mb-2 text-[11px]">
            <User className="w-3 h-3" style={{ color:'#D4AF37' }} strokeWidth={1.5} />
            <span style={{ color:'#595959' }}>Assigned: <span style={{ color:'#0F0F0F', fontWeight:500 }}>{assignee}</span></span>
          </div>
        )}
        <div className="flex items-center gap-1.5 mb-2">
          <button data-testid={`view-details-${order.order_id}`} onClick={() => setDetailsModal(order)}
            className="flex-1 flex items-center justify-center gap-1 px-2.5 py-2 text-[11px] font-medium"
            style={{ backgroundColor:'#0F0F0F', color:'#FFF' }}>
            <Eye className="w-3.5 h-3.5" strokeWidth={1.5} /> View Details
          </button>
          <button data-testid={`view-measurements-${order.order_id}`} onClick={() => setMeasurementModal(order)}
            className="flex items-center gap-1 px-2.5 py-2 text-[10px] border" style={{ borderColor:'#E5E5E5', color:'#595959' }}>
            <Ruler className="w-3 h-3" strokeWidth={1.5} /> Sizes
          </button>
          {!['completed','delivered','cancelled'].includes(order.status) && (
            <button data-testid={`assign-btn-${order.order_id}`} onClick={() => setAssignModal(order.order_id)}
              className="flex items-center gap-1 px-2.5 py-2 text-[10px] border" style={{ borderColor:'#E5E5E5', color:'#595959' }}>
              <Users className="w-3 h-3" strokeWidth={1.5} />
            </button>
          )}
          {order.customer_phone_masked && (
            <button data-testid={`call-btn-${order.order_id}`}
              onClick={(e) => { e.stopPropagation(); setDetailsModal(order); setTimeout(() => document.querySelector("[data-testid='details-call-btn']")?.click(), 60); }}
              className="flex items-center gap-1 px-2.5 py-2 text-[10px] border" style={{ borderColor:'#E5E5E5', color:'#595959' }}>
              <Phone className="w-3 h-3" strokeWidth={1.5} />
            </button>
          )}
        </div>
        {['placed','assigned'].includes(order.status) && (
          <div className="flex gap-1.5">
            <button data-testid={`accept-btn-${order.order_id}`} onClick={() => handleAccept(order.order_id)}
              className="flex-1 py-2 text-[11px] flex items-center justify-center gap-1 text-white" style={{ background:'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)' }}>
              <Check className="w-3.5 h-3.5" strokeWidth={2} /> Accept
            </button>
            <button data-testid={`counter-btn-${order.order_id}`} onClick={() => { setCounterModal(order.order_id); setCounterDate(''); }}
              className="flex-1 py-2 text-[11px] flex items-center justify-center gap-1 border" style={{ borderColor:'#E5E5E5', color:'#595959' }}>
              <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} /> Counter
            </button>
            <button data-testid={`reject-btn-${order.order_id}`} onClick={() => handleReject(order.order_id)}
              className="py-2 px-3 text-[11px] flex items-center justify-center border" style={{ borderColor:'#E5E5E5', color:'#595959' }}>
              <X className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
          </div>
        )}
        {order.status==='stitching' && (
          <button data-testid={`start-qc-btn-${order.order_id}`} onClick={() => handleMarkReady(order.order_id)} className="w-full py-2 text-[11px] flex items-center justify-center gap-1" style={{ backgroundColor:'#0F0F0F', color:'#FFF' }}>
            <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5} /> Complete Stitching · Start QC
          </button>
        )}
        {order.status==='qc_pending' && (
          <div data-testid={`qc-inline-panel-${order.order_id}`} className="mt-1 pt-3 border-t" style={{ borderColor:'#E5E5E5' }}>
            <QCInlineForm order={order} onComplete={loadDashboard} />
          </div>
        )}
        {order.status==='qc_approved' && (
          <button data-testid={`dispatch-btn-${order.order_id}`} onClick={() => handleDispatch(order.order_id)} className="w-full py-2 text-[11px] flex items-center justify-center gap-1 text-white" style={{ background:'linear-gradient(135deg, #166534 0%, #0F0F0F 100%)' }}>
            <Truck className="w-3.5 h-3.5" strokeWidth={1.5} /> Mark Ready for Dispatch
          </button>
        )}
        {order.status==='ready' && (
          <button onClick={() => handleMarkCompleted(order.order_id)} className="w-full py-2 text-[11px] flex items-center justify-center gap-1 text-white" style={{ background:'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)' }}>
            <Truck className="w-3.5 h-3.5" strokeWidth={1.5} /> Mark Delivered
          </button>
        )}
        {['completed','delivered'].includes(order.status) && (
          <div className="flex items-center gap-1.5 text-[11px] py-1.5">
            <CheckCircle className="w-3.5 h-3.5" style={{ color:'#166534' }} strokeWidth={1.5} />
            <span style={{ color:'#166534' }}>Delivered</span>
          </div>
        )}
      </div>
    );
  };

  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor:'#FAF9F6' }}>
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor:'#D4AF37', borderTopColor:'transparent' }} />
      </div>
    );
  }

  return (
    <div data-testid="tailor-dashboard" className="min-h-screen pb-4" style={{ backgroundColor:'#FAF9F6', fontFamily:'Outfit, sans-serif' }}>
      <div className="sticky top-0 z-50 flex items-center justify-between px-3 py-3 border-b bg-white" style={{ borderColor:'#E5E5E5' }}>
        <div>
          <h1 className="text-base" style={{ fontFamily:'Playfair Display, serif', color:'#0F0F0F' }}>Dashboard</h1>
          <p className="text-[11px]" style={{ color:'#595959' }}>{profile.name || 'Tailor'}</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="mute-toggle" onClick={() => { const n={...soundSettings, soundEnabled:!soundSettings.soundEnabled}; setSoundSettings(n); saveSettings(n); }} className="p-1.5">
            {isMuted ? <VolumeX className="w-[18px] h-[18px]" style={{ color:'#991B1B' }} strokeWidth={1.5} /> : <Volume2 className="w-[18px] h-[18px]" style={{ color:'#595959' }} strokeWidth={1.5} />}
          </button>
          <button data-testid="sound-settings-btn" onClick={() => navigate('/tailor-settings')} className="p-1.5">
            <Settings className="w-[18px] h-[18px]" style={{ color:'#595959' }} strokeWidth={1.5} />
          </button>
          <button data-testid="notifications-btn" onClick={() => navigate('/tailor-notifications')} className="p-1.5 relative">
            <Bell className="w-[18px] h-[18px]" style={{ color:'#0F0F0F' }} strokeWidth={1.5} />
            {lateOrders.length > 0 && <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full" style={{ backgroundColor:'#991B1B' }} />}
          </button>
          <button onClick={async () => { await logout(); navigate('/tailor-login'); }} className="w-7 h-7 rounded-full overflow-hidden border" style={{ borderColor:'#E5E5E5' }}>
            <User className="w-full h-full p-1" style={{ color:'#595959' }} />
          </button>
        </div>
      </div>

      <div className="px-3 py-3 space-y-3">
        {lateOrders.length > 0 && (
          <div data-testid="late-orders-reminder" className="p-3 border flex items-start gap-2" style={{ backgroundColor:'rgba(153,27,27,0.04)', borderColor:'rgba(153,27,27,0.15)' }}>
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color:'#991B1B' }} strokeWidth={1.5} />
            <div>
              <p className="text-[12px] font-medium" style={{ color:'#991B1B' }}>{lateOrders.length} overdue</p>
              <p className="text-[11px]" style={{ color:'#595959' }}>{lateOrders.map(o => o.order_id).join(', ')}</p>
            </div>
          </div>
        )}

        <div className="bg-white border p-3 flex items-center justify-between" style={{ borderColor:'#E5E5E5' }}>
          <div>
            <p className="text-[13px] font-medium" style={{ color:'#0F0F0F' }}>{isMuted?'Muted':isOverloaded?'Paused':isAvailable?'Online':'Offline'}</p>
            <p className="text-[11px]" style={{ color: isMuted?'#991B1B':'#595959' }}>{isMuted?'Alerts silenced':`${activeCount}/${MAX_ACTIVE_ORDERS} active`}</p>
          </div>
          <button data-testid="availability-toggle" onClick={handleToggleAvailability}>
            {isAvailable && !isOverloaded ? <ToggleRight className="w-9 h-9" style={{ color:'#166534' }} strokeWidth={1.5} /> : <ToggleLeft className="w-9 h-9" style={{ color:'#595959' }} strokeWidth={1.5} />}
          </button>
        </div>

        <div data-testid="earnings-breakdown" className="bg-white border p-3" style={{ borderColor:'#E5E5E5' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5" style={{ color:'#D4AF37' }} strokeWidth={1.5} />
              <p className="text-[11px] tracking-[0.1em] uppercase" style={{ color:'#595959' }}>Earnings</p>
            </div>
            <div className="flex bg-[#FAF9F6]">
              {['today','weekly','monthly'].map(p => (
                <button key={p} data-testid={`earnings-${p}-btn`} onClick={() => setEarningsPeriod(p)}
                  className="px-2.5 py-1 text-[10px] tracking-[0.05em] uppercase transition-all"
                  style={{ backgroundColor: earningsPeriod===p?'#0F0F0F':'transparent', color: earningsPeriod===p?'#FFF':'#595959' }}>{p}</button>
              ))}
            </div>
          </div>
          <div className="flex items-end gap-1.5">
            <p className="text-2xl" style={{ fontFamily:'Playfair Display, serif', color:'#0F0F0F' }}>{'\u20B9'}{(earnings[earningsPeriod]||0).toLocaleString('en-IN')}</p>
          </div>
        </div>

        <div className="bg-white border" style={{ borderColor:'#E5E5E5' }}>
          <button data-testid="team-toggle-btn" onClick={() => setShowTeamPanel(!showTeamPanel)} className="w-full px-3 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" style={{ color:'#D4AF37' }} strokeWidth={1.5} />
              <p className="text-[11px] tracking-[0.1em] uppercase" style={{ color:'#595959' }}>Team ({staff.length})</p>
            </div>
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showTeamPanel?'rotate-180':''}`} style={{ color:'#595959' }} strokeWidth={1.5} />
          </button>
          {showTeamPanel && (
            <div className="px-3 pb-3 border-t" style={{ borderColor:'#E5E5E5' }}>
              <div className="space-y-2 mt-2">
                {staff.map(m => (
                  <div key={m.id} className="flex items-center gap-2.5 p-2.5" style={{ backgroundColor:'#FAF9F6' }}>
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 text-[10px] text-white font-medium" style={{ background:'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)' }}>
                      {m.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-medium truncate" style={{ color:'#0F0F0F' }}>{m.name}</p>
                      <p className="text-[10px]" style={{ color:'#595959' }}>{m.role}</p>
                    </div>
                    <p className="text-[10px]" style={{ color:'#595959' }}>{m.completed} done</p>
                  </div>
                ))}
              </div>
              {!showAddStaff ? (
                <button data-testid="add-staff-toggle-btn" onClick={() => setShowAddStaff(true)} className="w-full mt-2 py-2 text-[11px] flex items-center justify-center gap-1 border border-dashed" style={{ borderColor:'#D4AF37', color:'#D4AF37' }}>
                  <UserPlus className="w-3 h-3" strokeWidth={1.5} /> Add Staff
                </button>
              ) : (
                <div className="mt-2 p-2.5 border" style={{ borderColor:'#E5E5E5' }}>
                  <input data-testid="staff-name-input" type="text" value={newStaffName} onChange={e => setNewStaffName(e.target.value)} placeholder="Name" className="w-full py-1.5 text-[12px] bg-transparent border-b focus:outline-none mb-2" style={{ borderColor:'#E5E5E5', color:'#0F0F0F' }} />
                  <input data-testid="staff-role-input" type="text" value={newStaffRole} onChange={e => setNewStaffRole(e.target.value)} placeholder="Role" className="w-full py-1.5 text-[12px] bg-transparent border-b focus:outline-none mb-2" style={{ borderColor:'#E5E5E5', color:'#0F0F0F' }} />
                  <div className="flex gap-2">
                    <button data-testid="save-staff-btn" onClick={handleAddStaff} className="flex-1 py-1.5 text-[11px] text-white" style={{ background:'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)' }}>Save</button>
                    <button onClick={() => { setShowAddStaff(false); setNewStaffName(''); setNewStaffRole(''); }} className="px-3 py-1.5 text-[11px] border" style={{ borderColor:'#E5E5E5', color:'#595959' }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white border p-3 flex items-center gap-3" style={{ borderColor:'#E5E5E5' }}>
          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ background:'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)' }}>
            <User className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium" style={{ color:'#0F0F0F' }}>{profile.name}</p>
            <div className="flex items-center gap-1.5">
              <Star className="w-3 h-3 fill-[#D4AF37]" style={{ color:'#D4AF37' }} />
              <span className="text-[11px]" style={{ color:'#0F0F0F' }}>{profile.rating || 0}</span>
              <span className="text-[10px]" style={{ color:'#595959' }}>({profile.reviews_count || 0})</span>
            </div>
          </div>
        </div>

        <div className="bg-white border" style={{ borderColor:'#E5E5E5' }}>
          <div data-testid="order-tabs" className="flex overflow-x-auto border-b scrollbar-hide" style={{ borderColor:'#E5E5E5' }}>
            {tabs.map(tab => (
              <button key={tab.id} data-testid={`tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
                className="flex-shrink-0 px-4 py-2.5 text-center transition-colors relative whitespace-nowrap"
                style={{ color: activeTab===tab.id?'#0F0F0F':'#595959' }}>
                <span className="text-[11px] tracking-[0.04em] uppercase">
                  {tab.label}
                  {tab.count > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9px] rounded-full" style={{ backgroundColor: tab.id==='overdue'&&tab.count>0?'#991B1B':activeTab===tab.id?'#0F0F0F':'#E5E5E5', color: tab.id==='overdue'&&tab.count>0?'#FFF':activeTab===tab.id?'#FFF':'#595959' }}>{tab.count}</span>}
                </span>
                {activeTab===tab.id && <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ background:'linear-gradient(90deg, #E5B2B9, #D4AF37)' }} />}
              </button>
            ))}
          </div>
          {filteredOrders.length > 0 ? (
            <div className="divide-y" style={{ borderColor:'#F0F0F0' }}>
              {filteredOrders.map(o => <OrderCard key={o.id || o.order_id} order={o} />)}
            </div>
          ) : (
            <div className="py-10 text-center">
              <Package className="w-7 h-7 mx-auto mb-2" style={{ color:'#E5E5E5' }} strokeWidth={1.5} />
              <p className="text-[12px]" style={{ color:'#595959' }}>No orders here</p>
            </div>
          )}
        </div>
      </div>

      {/* Counter Modal */}
      {counterModal && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" data-testid="counter-offer-modal">
          <div className="absolute inset-0 bg-black/30" onClick={() => setCounterModal(null)} style={{ backdropFilter:'blur(4px)' }} />
          <div className="relative w-full sm:max-w-sm mx-0 sm:mx-4 bg-white p-5 shadow-xl animate-modalUp" style={{ borderTopLeftRadius:16, borderTopRightRadius:16 }}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base" style={{ fontFamily:'Playfair Display, serif', color:'#0F0F0F' }}>Counter Offer</h3>
              <button onClick={() => setCounterModal(null)}><X className="w-5 h-5" style={{ color:'#595959' }} strokeWidth={1.5} /></button>
            </div>
            <input data-testid="counter-date-modal-input" type="date" value={counterDate} onChange={e => setCounterDate(e.target.value)} min={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2.5 text-[13px] bg-white border mb-4 focus:outline-none" style={{ borderColor:'#E5E5E5', color:'#0F0F0F' }} />
            <div className="flex gap-2">
              <button data-testid="send-counter-modal-btn" onClick={handleSendCounter} disabled={!counterDate} className="flex-1 py-2.5 text-[11px] uppercase text-white flex items-center justify-center gap-1.5 disabled:opacity-40" style={{ background:'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)' }}>
                <Send className="w-3.5 h-3.5" strokeWidth={1.5} /> Send
              </button>
              <button onClick={() => setCounterModal(null)} className="px-5 py-2.5 text-[11px] border" style={{ borderColor:'#E5E5E5', color:'#595959' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Measurements Modal */}
      {measurementModal && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" data-testid="measurements-modal">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMeasurementModal(null)} style={{ backdropFilter:'blur(4px)' }} />
          <div className="relative w-full sm:max-w-sm mx-0 sm:mx-4 bg-white p-5 shadow-xl animate-modalUp" style={{ borderTopLeftRadius:16, borderTopRightRadius:16 }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base" style={{ fontFamily:'Playfair Display, serif', color:'#0F0F0F' }}>Measurements</h3>
              <button onClick={() => setMeasurementModal(null)}><X className="w-5 h-5" style={{ color:'#595959' }} strokeWidth={1.5} /></button>
            </div>
            <p className="text-[12px] mb-3 font-medium" style={{ color:'#0F0F0F' }}>{measurementModal.customer_name} — {measurementModal.product}</p>
            <div className="space-y-2">
              {Object.entries(measurementModal.measurements || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between py-2 border-b" style={{ borderColor:'#F0F0F0' }}>
                  <span className="text-[12px] capitalize" style={{ color:'#595959' }}>{k}</span>
                  <span data-testid={`quick-measure-${k}`} className="text-[13px] font-medium" style={{ color:'#0F0F0F' }}>{v}{typeof v === 'number' ? ' cm' : ''}</span>
                </div>
              ))}
            </div>
            <button onClick={() => setMeasurementModal(null)} className="w-full mt-4 py-2.5 text-[11px] uppercase" style={{ backgroundColor:'#0F0F0F', color:'#FFF' }}>Close</button>
          </div>
        </div>
      )}

      {/* ─── FULL ORDER DETAILS MODAL ─── */}
      {detailsModal && (() => {
        const o = detailsModal;
        const c = o.customizations || {};
        const m = o.measurements || {};
        const refImages = c.reference_images || c.referenceImages || [];
        const Row = ({ label, value }) => (
          <div className="flex items-start justify-between gap-3 py-1.5">
            <span className="text-[10px] tracking-[0.06em] uppercase flex-shrink-0" style={{ color:'#8a8a8a' }}>{label}</span>
            <span className="text-[12px] text-right" style={{ color:'#0F0F0F', fontWeight:500 }}>{value || '—'}</span>
          </div>
        );
        return (
          <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center" data-testid="order-details-modal">
            <div className="absolute inset-0 bg-black/40" onClick={() => setDetailsModal(null)} style={{ backdropFilter:'blur(6px)' }} />
            <div className="relative w-full sm:max-w-md mx-0 sm:mx-4 bg-white shadow-2xl animate-modalUp max-h-[92vh] overflow-y-auto" style={{ borderTopLeftRadius:16, borderTopRightRadius:16 }}>
              {/* Hero header */}
              <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b bg-white" style={{ borderColor:'#E5E5E5' }}>
                <div className="min-w-0">
                  <p className="text-[9px] tracking-[0.14em] uppercase" style={{ color:'#D4AF37' }}>Order Details</p>
                  <h3 className="text-[15px] truncate" style={{ fontFamily:'Playfair Display, serif', color:'#0F0F0F' }}>{o.order_id}</h3>
                </div>
                <button data-testid="details-close" onClick={() => setDetailsModal(null)} className="p-1">
                  <X className="w-5 h-5" style={{ color:'#595959' }} strokeWidth={1.5} />
                </button>
              </div>

              <div className="px-4 py-3">
                {/* Customer strip */}
                <div className="flex items-center gap-2 p-3 mb-3" style={{ backgroundColor:'#FAF9F6' }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor:'#D4AF37' }}>
                    <User className="w-5 h-5 text-white" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px]" style={{ color:'#0F0F0F', fontWeight:500 }}>{o.customer_name || 'Customer'}</p>
                    <p className="text-[11px]" style={{ color:'#595959' }}>
                      {o.customer_phone_masked || 'Phone private'}
                      <span className="inline-flex items-center gap-0.5 ml-1.5 text-[9px] px-1 py-0.5" style={{ backgroundColor:'rgba(22,101,52,0.08)', color:'#166534' }}>
                        <Shield className="w-2.5 h-2.5" strokeWidth={1.5} /> Masked
                      </span>
                    </p>
                  </div>
                  {o.customer_phone_masked && (
                    <button data-testid="details-call" onClick={() => handleMaskedCall(o.order_id)}
                      className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor:'#166534' }}>
                      <Phone className="w-4 h-4 text-white" strokeWidth={1.5} />
                    </button>
                  )}
                </div>

                {/* Product block */}
                <div className="mb-3">
                  <p className="text-[9px] tracking-[0.14em] uppercase mb-1.5" style={{ color:'#8a8a8a' }}>Product</p>
                  <div className="p-3 border" style={{ borderColor:'#E5E5E5' }}>
                    <Row label="Product Type" value={o.product} />
                    <Row label="Category" value={o.category} />
                    {c.subcategory && <Row label="Subcategory" value={c.subcategory} />}
                  </div>
                </div>

                {/* Customizations — now with image gallery */}
                {(() => {
                  const designEntries = Object.entries(c).filter(([k, v]) => {
                    if (['reference_images','referenceImages','subcategory'].includes(k)) return false;
                    return v && (typeof v === 'string' || (typeof v === 'object' && (v.label || v.image)));
                  });
                  if (designEntries.length === 0) return null;

                  const getLabel = (v) => typeof v === 'object' ? (v.label || '') : String(v);
                  const getImage = (v) => typeof v === 'object' ? (v.image || '') : '';

                  const imageDesigns = designEntries.filter(([, v]) => getImage(v));
                  const textOnly = designEntries.filter(([, v]) => !getImage(v));

                  return (
                    <div className="mb-3">
                      <p className="text-[9px] tracking-[0.14em] uppercase mb-1.5" style={{ color:'#8a8a8a' }}>Design Selections</p>

                      {/* Image gallery for selections with images */}
                      {imageDesigns.length > 0 && (
                        <div className="grid grid-cols-3 gap-2 mb-2" data-testid="customer-design-gallery">
                          {imageDesigns.map(([k, v]) => (
                            <button key={k} data-testid={`design-img-${k}`}
                              onClick={() => setZoomImage({ src: getImage(v), label: `${k.replace(/_/g,' ')} — ${getLabel(v)}` })}
                              className="aspect-[3/4] bg-[#FAF9F6] overflow-hidden border relative group"
                              style={{ borderColor:'#E5E5E5' }}>
                              <img src={getImage(v)} alt={getLabel(v)} loading="lazy"
                                className="w-full h-full object-cover" />
                              <div className="absolute inset-x-0 bottom-0 p-1.5 bg-gradient-to-t from-black/80 to-transparent">
                                <p className="text-[8px] tracking-[0.08em] uppercase text-white/70">{k.replace(/_/g,' ')}</p>
                                <p className="text-[10px] text-white leading-tight line-clamp-1">{getLabel(v)}</p>
                              </div>
                              <div className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor:'rgba(255,255,255,0.9)' }}>
                                <Eye className="w-3 h-3" style={{ color:'#0F0F0F' }} strokeWidth={1.5} />
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Text-only selections */}
                      {textOnly.length > 0 && (
                        <div className="p-3 border" style={{ borderColor:'#E5E5E5' }}>
                          {textOnly.map(([k, v]) => (
                            <Row key={k} label={k.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase())} value={getLabel(v)} />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Customer Memory — Phase 2: tailoring history & alteration memory */}
                {o.customer_id && (
                  <CustomerHistoryPanel
                    customerId={o.customer_id}
                    currentOrderId={o.order_id}
                  />
                )}

                {/* Measurements — tailoring-grade visual guide */}
                {Object.keys(m).length > 0 && (
                  <div className="mb-3" data-testid="details-measurement-guide">
                    <MeasurementGuide
                      orderId={o.order_id}
                      category={o.category || o.product}
                      measurements={m}
                      provenance={o.measurement_provenance || null}
                      customizations={c}
                      fitConfidence={o.fit_confidence ?? c.fit_confidence}
                      onRequestClarification={() => handleMaskedCall(o.order_id)}
                    />
                  </div>
                )}

                <OrderScanMeasurements order={o} />

                {/* Custom Notes */}
                {o.notes && (
                  <div className="mb-3">
                    <p className="text-[9px] tracking-[0.14em] uppercase mb-1.5" style={{ color:'#8a8a8a' }}>Customer Notes</p>
                    <div className="p-3 border text-[12px]" style={{ borderColor:'#E5E5E5', color:'#0F0F0F', lineHeight:1.5 }}>
                      {o.notes}
                    </div>
                  </div>
                )}

                {/* Reference Images */}
                {refImages.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[9px] tracking-[0.14em] uppercase mb-1.5" style={{ color:'#8a8a8a' }}>Reference Images</p>
                    <div className="grid grid-cols-3 gap-2">
                      {refImages.map((src, i) => (
                        <a key={i} href={src} target="_blank" rel="noreferrer" className="aspect-square overflow-hidden bg-[#FAF9F6] border" style={{ borderColor:'#E5E5E5' }}>
                          <img src={src} alt={`Ref ${i+1}`} className="w-full h-full object-cover" loading="lazy" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Delivery + Price */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="p-3 border" style={{ borderColor:'#E5E5E5' }}>
                    <p className="text-[9px] tracking-[0.14em] uppercase mb-1" style={{ color:'#8a8a8a' }}>Delivery Date</p>
                    <p className="text-[13px]" style={{ color:'#0F0F0F', fontWeight:500 }}>{formatDate(o.delivery_date)}</p>
                  </div>
                  <div className="p-3 border" style={{ borderColor:'#D4AF37', backgroundColor:'rgba(212,175,55,0.04)' }}>
                    <p className="text-[9px] tracking-[0.14em] uppercase mb-1" style={{ color:'#8a8a8a' }}>Order Total</p>
                    <p className="text-[16px]" style={{ fontFamily:'Playfair Display, serif', color:'#0F0F0F' }}>
                      ₹{(o.total_price||0).toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="grid grid-cols-2 gap-2 mb-2">
                  {['placed','assigned'].includes(o.status) && (
                    <button data-testid="details-accept"
                      onClick={() => { handleAccept(o.order_id); setDetailsModal(null); }}
                      className="col-span-2 py-3 text-[11px] tracking-[0.08em] uppercase flex items-center justify-center gap-1.5 text-white"
                      style={{ background:'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)' }}>
                      <Check className="w-4 h-4" strokeWidth={2} /> Accept Order
                    </button>
                  )}
                  {o.status === 'stitching' && (
                    <button data-testid="details-mark-ready"
                      onClick={() => { handleMarkReady(o.order_id); setDetailsModal(null); }}
                      className="col-span-2 py-3 text-[11px] tracking-[0.08em] uppercase flex items-center justify-center gap-1.5 text-white"
                      style={{ backgroundColor:'#0F0F0F' }}>
                      <CheckCircle className="w-4 h-4" strokeWidth={1.5} /> Mark In Progress / Ready
                    </button>
                  )}
                  {o.status === 'ready' && (
                    <button data-testid="details-mark-completed"
                      onClick={() => { handleMarkCompleted(o.order_id); setDetailsModal(null); }}
                      className="col-span-2 py-3 text-[11px] tracking-[0.08em] uppercase flex items-center justify-center gap-1.5 text-white"
                      style={{ background:'linear-gradient(135deg, #166534 0%, #0F4827 100%)' }}>
                      <Truck className="w-4 h-4" strokeWidth={1.5} /> Mark Completed
                    </button>
                  )}
                  {o.customer_phone_masked && (
                    <button data-testid="details-call-btn" onClick={() => handleMaskedCall(o.order_id)}
                      disabled={callingBusy}
                      className="py-3 text-[11px] tracking-[0.08em] uppercase flex items-center justify-center gap-1.5 border disabled:opacity-50"
                      style={{ borderColor:'#166534', color:'#166534' }}>
                      <Phone className="w-4 h-4" strokeWidth={1.5} /> {callingBusy ? 'Connecting…' : 'Call Customer'}
                    </button>
                  )}
                  {o.status === 'counter-offered' && (
                    <div className={o.customer_phone_masked ? '' : 'col-span-2'}>
                      <p className="py-3 text-[11px] text-center" style={{ color:'#D4AF37', backgroundColor:'rgba(212,175,55,0.06)' }}>
                        Awaiting customer approval for counter date
                      </p>
                    </div>
                  )}
                  {['completed','delivered'].includes(o.status) && (
                    <div className={o.customer_phone_masked ? '' : 'col-span-2'}>
                      <p className="py-3 text-[11px] text-center flex items-center justify-center gap-1.5" style={{ color:'#166534', backgroundColor:'rgba(22,101,52,0.06)' }}>
                        <CheckCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> Order Delivered
                      </p>
                    </div>
                  )}
                </div>

                <button onClick={() => setDetailsModal(null)}
                  className="w-full py-2.5 text-[10px] tracking-[0.08em] uppercase border mt-1"
                  style={{ borderColor:'#E5E5E5', color:'#595959' }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Assign Modal */}
      {assignModal && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" data-testid="assign-staff-modal">
          <div className="absolute inset-0 bg-black/30" onClick={() => setAssignModal(null)} style={{ backdropFilter:'blur(4px)' }} />
          <div className="relative w-full sm:max-w-sm mx-0 sm:mx-4 bg-white p-5 shadow-xl animate-modalUp" style={{ borderTopLeftRadius:16, borderTopRightRadius:16 }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base" style={{ fontFamily:'Playfair Display, serif', color:'#0F0F0F' }}>Assign to Staff</h3>
              <button onClick={() => setAssignModal(null)}><X className="w-5 h-5" style={{ color:'#595959' }} strokeWidth={1.5} /></button>
            </div>
            <div className="space-y-2">
              {staff.map(m => (
                <button key={m.id} onClick={() => handleAssignStaff(assignModal, m.id)}
                  className="w-full flex items-center gap-2.5 p-3 border hover:border-[#D4AF37]" style={{ borderColor:'#E5E5E5' }}>
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 text-[10px] text-white font-medium" style={{ background:'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)' }}>
                    {m.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-[12px] font-medium" style={{ color:'#0F0F0F' }}>{m.name}</p>
                    <p className="text-[10px]" style={{ color:'#595959' }}>{m.role}</p>
                  </div>
                  <ChevronRight className="w-4 h-4" style={{ color:'#BFBFBF' }} strokeWidth={1.5} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes modalUp { from { transform:translateY(20px); opacity:0; } to { transform:translateY(0); opacity:1; } }
        @keyframes fadeIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
        .animate-modalUp { animation: modalUp 0.25s ease-out; }
        .scrollbar-hide::-webkit-scrollbar { display:none; }
        .scrollbar-hide { -ms-overflow-style:none; scrollbar-width:none; }
      `}</style>

      {/* ─── Image Zoom Modal ─── */}
      {zoomImage && (
        <div data-testid="zoom-modal" className="fixed inset-0 z-[120] flex items-center justify-center p-4"
          style={{ backgroundColor:'rgba(0,0,0,0.85)', backdropFilter:'blur(8px)' }}
          onClick={() => setZoomImage(null)}>
          <button data-testid="zoom-close"
            onClick={(e) => { e.stopPropagation(); setZoomImage(null); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center bg-white/95">
            <X className="w-5 h-5" style={{ color:'#0F0F0F' }} strokeWidth={1.5} />
          </button>
          <div className="max-w-full max-h-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img src={zoomImage.src} alt={zoomImage.label}
              className="max-w-full max-h-[80vh] object-contain" />
            <p className="text-[11px] text-white/90 tracking-[0.1em] uppercase mt-3">{zoomImage.label}</p>
          </div>
        </div>
      )}

      {/* ─── Masked Call Session Modal ─── */}
      {callSession && (
        <div data-testid="call-session-modal" className="fixed inset-0 z-[115] flex items-center justify-center px-4"
          style={{ backgroundColor:'rgba(0,0,0,0.55)', backdropFilter:'blur(6px)' }}
          onClick={() => setCallSession(null)}>
          <div className="bg-white w-full max-w-sm p-6 text-center" onClick={(e) => e.stopPropagation()}>
            {callSession.error ? (
              <>
                <div className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3" style={{ backgroundColor:'rgba(153,27,27,0.1)' }}>
                  <X className="w-6 h-6" style={{ color:'#991B1B' }} strokeWidth={1.5} />
                </div>
                <p className="text-[13px]" style={{ color:'#991B1B' }}>{callSession.error}</p>
              </>
            ) : (
              <>
                <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-3" style={{ background:'linear-gradient(135deg, #166534 0%, #0F4827 100%)' }}>
                  <Phone className="w-7 h-7 text-white animate-pulse" strokeWidth={1.5} />
                </div>
                <p className="text-[10px] tracking-[0.14em] uppercase mb-1" style={{ color:'#D4AF37' }}>Masked Call Ready</p>
                <h3 className="text-[18px] mb-2" style={{ fontFamily:'Playfair Display, serif', color:'#0F0F0F' }}>
                  {callSession.proxy_number}
                </h3>
                <div className="flex items-center justify-center gap-1 mb-3">
                  <Shield className="w-3 h-3" style={{ color:'#166534' }} strokeWidth={1.5} />
                  <span className="text-[10px]" style={{ color:'#166534' }}>Customer's number stays private</span>
                </div>
                <p className="text-[11px] mb-4" style={{ color:'#595959', lineHeight:1.5 }}>
                  {callSession.message || 'Dial this proxy number — it will privately route to the customer.'}
                </p>
                <a data-testid="call-session-dial" href={`tel:${callSession.proxy_number}`}
                  className="inline-flex items-center justify-center gap-2 w-full py-3 text-[11px] tracking-[0.08em] uppercase text-white"
                  style={{ background:'linear-gradient(135deg, #166534 0%, #0F4827 100%)' }}>
                  <Phone className="w-4 h-4" strokeWidth={1.5} /> Dial Proxy
                </a>
              </>
            )}
            <button data-testid="call-session-close" onClick={() => setCallSession(null)}
              className="mt-3 w-full py-2 text-[10px] tracking-[0.08em] uppercase border"
              style={{ borderColor:'#E5E5E5', color:'#595959' }}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TailorDashboard;
